import React, { useCallback, useState } from 'react';

import { managementAddUserToAccountByUniqueIdentifier, managementUpdateUserByUniqueIdentifier } from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';
import { User } from '../../core/_models';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { AccountFast } from '../../core/_models_fast';
import AccountSelect from '../../components/AccountSelect';

export default function ManageUserForm({
    user = undefined,
    setUser = undefined
}: {
    user?: User,
    setUser?: (user: User) => void
}) {
    const axios = useAxios();

    const {
        currentUserAccountUniqueIds
    } = useAuth();

    const navigate = useNavigate();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    const [selectedAccount, setSelectedAccount] = React.useState<AccountFast | null>(null);

    const onFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        setSaveSuccess(null);
        setErrorSaving(null);
        setSaving(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        if (user) {
            try {
                const {
                    user: updatedUser
                } = await managementUpdateUserByUniqueIdentifier(axios, user.unique_identifier, {
                    ...data,
                })
                if (!setUser) {
                    throw new Error('setUser function is required to update user')
                }
                setUser(updatedUser);
                setSaveSuccess('User updated successfully.')
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setErrorSaving(errorMessage)
                setSaving(false)
            }
        } else {
            try {
                if (!selectedAccount) {
                    throw new APIError('You must select an account for this user.', {})
                }
                await managementAddUserToAccountByUniqueIdentifier(axios, selectedAccount.unique_identifier, {
                    ...data,
                })
                // Rather than navigating we're going to show the setup token modal...
                navigate(`/management/accounts/${selectedAccount.unique_identifier}/users`)
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setErrorSaving(errorMessage)
                setSaving(false)
            }
        }
    }, [saving, user, axios, setUser, selectedAccount?.unique_identifier, navigate]);

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <form className="card" onSubmit={onFormSubmitted}>
                    <div className="card-header">
                        {user ? <h3 className="card-title">
                            {`Manage ${user.public_data.name || 'User'}`}
                        </h3> : <h3 className="card-title">
                            Add User
                        </h3>}
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="user_full_name"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`User's name (required)`}
                                </label>
                                <input
                                    id="user_full_name"
                                    name="user_full_name"
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="John Doe"
                                    required
                                    autoComplete='off'
                                    defaultValue={user?.public_data.name || ''}
                                    autoFocus={!user}
                                />
                                {false && 'this is a subtitle' && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    e.g. narthex, back chapel, viewing room, etc.
                                </div>}
                            </div>
                            {/* Email address: */}
                            <div>
                                <label
                                    htmlFor="user_email"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`User's email address (required)`}
                                </label>
                                <input
                                    id="user_email"
                                    name="user_email"
                                    type="email"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder="john@doe.com"
                                    required
                                    autoComplete='off'
                                    defaultValue={user ? user.email : ''}
                                    readOnly={!!user}
                                    autoFocus={false}
                                />
                                {false && 'this is a subtitle' && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    e.g. narthex, back chapel, viewing room, etc.
                                </div>}
                            </div>
                            {!user && <>
                                <div>
                                    <label
                                        htmlFor="account_unique_identifier"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {`Add user to account (required)`}
                                    </label>
                                    <AccountSelect
                                        required
                                        name="account_unique_identifier"
                                        onChange={(account) => {
                                            if (account) {
                                                setSelectedAccount(account);
                                            } else {
                                                setSelectedAccount(null);
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="send_welcome_email"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {`Send welcome email`}
                                    </label>
                                    <input
                                        id="send_welcome_email"
                                        name="send_welcome_email"
                                        type="checkbox"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        defaultChecked={true}
                                    />
                                    {false && 'this is a subtitle' && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                        e.g. narthex, back chapel, viewing room, etc.
                                    </div>}
                                </div>
                            </>}
                        </div>
                    </div>
                    <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                        <div>
                            {errorSaving && <div className="text-red-500 text-sm font-medium">{errorSaving}</div>}
                            {saveSuccess && <div className="text-green-500 text-sm font-medium">{saveSuccess}</div>}
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={saving}
                            >
                                {user ? <>
                                    {!saving ? `Update User` : `Updating User...`}
                                </> : <>
                                    {!saving ? `Add User` : `Adding User...`}
                                </>}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
}